/**
 * The Consultation no-show queue (BAR-2809): patients whose latest consult was
 * missed or cancelled, with nothing upcoming booked. A reschedule is recorded as
 * a cancellation (Orbit and Healthie both do), so it isn't a miss.
 */

import { Appointment } from '@medplum/fhirtypes';
import { appointmentIsUpcoming } from './pac-list-appointments';
import { isAppointmentRescheduled } from './scheduling';

export interface NoShowLead {
  /** The latest missed consult, which describes the row. */
  consult: Appointment;
  /** Every missed consult, this one included. */
  missCount: number;
}

export function isMissedConsult(consult: Appointment): boolean {
  return consult.status === 'noshow' || (consult.status === 'cancelled' && !isAppointmentRescheduled(consult));
}

/** Judged on all of one patient's consults, NP and PN alike. */
export function noShowLead(consults: Appointment[], now: Date): NoShowLead | undefined {
  const counted = consults.filter(
    (consult) =>
      consult.status !== 'entered-in-error' && !(consult.status === 'cancelled' && isAppointmentRescheduled(consult))
  );

  if (counted.some((consult) => appointmentIsUpcoming(consult, now))) {
    return undefined;
  }

  const latest = [...counted].sort((a, b) => (b.start ?? '').localeCompare(a.start ?? ''))[0];
  if (!latest || !isMissedConsult(latest)) {
    return undefined;
  }

  return { consult: latest, missCount: counted.filter(isMissedConsult).length };
}
